import { Text, Modal } from "@mantine/core";
import {
  InputGroup,
  ForgotPasswordInput,
  LargeBtn,
  color,
} from "@funded-here-interface/common";
import { ChangeEvent, FC, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  setInvestorToken,
  setUserID,
  setUserSpv,
  setOnboardingType,
  setUserEmail,
  setRole,
  setOrgId,
  setUserFirstName,
  setUserLastName,
  setAgreedToMembershipAgreement,
  setOrgRoleId,
  setIsAuthRep,
  setHasCreditLimit,
  setEntityType,
  setIsAdminAuthRep,
  resetAuthState,
  setSpvName,
  setSpvDetails,
  setAgreedToSpvMembershipAgreement,
  setOrgName,
} from "@funded-here-interface/common/src/features/auth/authSlice_Investor";
import { useStyles } from "./LoginCss";
import { ToastContainer, toast } from "react-toastify";
import useLoginMutation from "@funded-here-interface/common/src/hooks/useLogin";
import "react-toastify/dist/ReactToastify.css";
import Flogo from "../../assets/Funded_Here_LogoA4.svg";
// import Flogo from "@funded-here-interface/common/src/assets/synlian_logo.png";
import {
  KYCStatus,
  Persona,
} from "@funded-here-interface/common/src/constant/enum";
import useGetUser from "@funded-here-interface/common/src/hooks/useGetUsers";
import { roleToPath } from "@funded-here-interface/common/src/Utils/navigation";
import { useDisclosure } from "@mantine/hooks";
import useVerifyTwoFAToken from "@funded-here-interface/common/src/hooks/useVerifyTwoFAToken";
import OtpField from "@funded-here-interface/common/src/components/OtpField/OtpField";

const Login: FC = () => {
  const { classes } = useStyles();
  const { redirectPath } = useParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [persona, setPersona] = useState("");
  const [error, setError] = useState({
    email_error: "",
    pass_error: "",
    persona_error: "",
  });
  const navigate = useNavigate();
  const mutation = useLoginMutation();
  const getUserMutation = useGetUser();
  const dispatch = useDispatch();
  const verifyTwoFATokenMutation = useVerifyTwoFAToken();
  const [openedVerifyOTP, modalAction] = useDisclosure(false);
  const [otp, setOtp] = useState("");

  const onChange = (value: string) => {
    setOtp(value);
  };

  const handleLogin = async () => {
    if (!persona) {
      toast.error("Select a persona");

      return;
    }
    if (email && password && error && persona) {
      mutation.mutate(
        { email, password, persona: persona },
        {
          onSuccess: async (data) => {
            if (data) {
              if (data.stage === "setup2FA") {
                const dataForQRCode = {
                  email: email.toLowerCase(),
                  persona: persona,
                  role: "Investor",
                };
                navigate("/two-auth-QR-code", { state: dataForQRCode });
              } else if (data.stage === "verifyOTP") {
                modalAction.open();
              } else {
                sessionStorage.setItem("Auth", data);
                const token = data;
                dispatch(setInvestorToken({ token }));
                getUserMutation.mutate(
                  { token },
                  {
                    onSuccess: (data) => {
                      const userDetail = data.user;
                      sessionStorage.setItem(
                        "orgRoleId",
                        userDetail.currentOrgRoleId
                      );

                      if (userDetail?.kycStatus === KYCStatus.REJECTED) {
                        toast.error("KYC Application has been Rejected.");
                        return;
                      }

                      if (
                        userDetail?.spvDetails?.kycStatus === KYCStatus.REJECTED
                      ) {
                        toast.error("SPV KYC Application has been Rejected.");
                        return;
                      }

                      const hasCreditLimit =
                        userDetail?.orgRole?.[0]?.creditLimit > 0;

                      dispatch(
                        setAgreedToMembershipAgreement({
                          agreedToMembershipAgreement:
                            userDetail?.agreedToMembershipAgreement,
                        })
                      );

                      dispatch(
                        setOrgName({
                          orgName: userDetail?.orgName,
                        })
                      );
                      dispatch(
                        setAgreedToSpvMembershipAgreement({
                          agreedToSpvMembershipAgreement:
                            userDetail?.agreedToSpvMembershipAgreement,
                        })
                      );
                      dispatch(
                        setHasCreditLimit({
                          hasCreditLimit,
                        })
                      );
                      dispatch(
                        setIsAuthRep({
                          isAuthRep: userDetail?.isAuthRep,
                        })
                      );
                      dispatch(
                        setIsAdminAuthRep({
                          isAdminAuthRep: userDetail?.isAdminAuthRep,
                        })
                      );
                      dispatch(
                        setEntityType({ entityType: userDetail?.entityType })
                      );
                      dispatch(setUserID({ userId: userDetail?.id }));
                      dispatch(setUserEmail({ userEmail: userDetail?.email }));
                      dispatch(
                        setUserSpv({ spvStatus: userDetail?.spvStatus })
                      );
                      dispatch(setSpvName({ spvName: userDetail?.spvName }));
                      dispatch(
                        setOnboardingType({
                          type: userDetail?.pendingOnBoardingAs,
                        })
                      );
                      dispatch(
                        setRole({ role: userDetail?.orgRole?.[0]?.role?.name })
                      );
                      dispatch(setOrgId({ orgId: userDetail?.organization }));
                      dispatch(
                        setOrgRoleId({
                          currentOrgRoleId: userDetail?.orgRoleId,
                        })
                      );
                      dispatch(
                        setUserFirstName({
                          userFirstName: userDetail?.firstName || null,
                        })
                      );
                      dispatch(
                        setUserLastName({
                          userLastName: userDetail?.lastName || null,
                        })
                      );
                      dispatch(
                        setSpvDetails({
                          spvDetails: userDetail?.spvDetails || null,
                        })
                      );

                      if (redirectPath) {
                        navigate(`/${redirectPath}`);
                      } else {
                        const path = roleToPath(userDetail);

                        if (path) {
                          navigate(path);
                        } else {
                          dispatch(resetAuthState());
                          toast.warn("No action required at this time");
                        }
                      }
                    },
                  }
                );
              }
            }
          },
          onError: (e) => {
            toast.error((e as Error).message);
          },
        }
      );
    } else {
      const newError = { ...error };
      if (!email) {
        newError.email_error = "Email is required";
      }
      if (!password) {
        newError.pass_error = "Password is required";
      }
      setError(newError);
    }
  };

  const handlePersonaChange = (e: any) => {
    setPersona(e.target.value);
    setError((prevState) => ({ ...prevState, persona_error: "" }));
  };

  const handleVerifyOtp = async () => {
    if (otp) {
      verifyTwoFATokenMutation.mutate(
        { token: otp, email: email.toLowerCase(), persona: persona },
        {
          onSuccess: (data) => {
            if (data) {
              sessionStorage.setItem("Auth", data);
              const token = data;
              dispatch(setInvestorToken({ token }));
              getUserMutation.mutate(
                { token },
                {
                  onSuccess: (data) => {
                    const userDetail = data.user;
                    sessionStorage.setItem(
                      "orgRoleId",
                      userDetail.currentOrgRoleId
                    );

                    if (userDetail?.kycStatus === KYCStatus.REJECTED) {
                      toast.error("KYC Application has been Rejected.");
                      return;
                    }

                    if (
                      userDetail?.spvDetails?.kycStatus === KYCStatus.REJECTED
                    ) {
                      toast.error("SPV KYC Application has been Rejected.");
                      return;
                    }

                    const hasCreditLimit =
                      userDetail?.orgRole?.[0]?.creditLimit > 0;

                    dispatch(
                      setAgreedToMembershipAgreement({
                        agreedToMembershipAgreement:
                          userDetail?.agreedToMembershipAgreement,
                      })
                    );

                    dispatch(
                      setOrgName({
                        orgName: userDetail?.orgName,
                      })
                    );
                    dispatch(
                      setAgreedToSpvMembershipAgreement({
                        agreedToSpvMembershipAgreement:
                          userDetail?.agreedToSpvMembershipAgreement,
                      })
                    );
                    dispatch(
                      setHasCreditLimit({
                        hasCreditLimit,
                      })
                    );
                    dispatch(
                      setIsAuthRep({
                        isAuthRep: userDetail?.isAuthRep,
                      })
                    );
                    dispatch(
                      setIsAdminAuthRep({
                        isAdminAuthRep: userDetail?.isAdminAuthRep,
                      })
                    );
                    dispatch(
                      setEntityType({ entityType: userDetail?.entityType })
                    );
                    dispatch(setUserID({ userId: userDetail?.id }));
                    dispatch(setUserEmail({ userEmail: userDetail?.email }));
                    dispatch(setUserSpv({ spvStatus: userDetail?.spvStatus }));
                    dispatch(setSpvName({ spvName: userDetail?.spvName }));
                    dispatch(
                      setOnboardingType({
                        type: userDetail?.pendingOnBoardingAs,
                      })
                    );
                    dispatch(
                      setRole({ role: userDetail?.orgRole?.[0]?.role?.name })
                    );
                    dispatch(setOrgId({ orgId: userDetail?.organization }));
                    dispatch(
                      setOrgRoleId({
                        currentOrgRoleId: userDetail?.orgRoleId,
                      })
                    );
                    dispatch(
                      setUserFirstName({
                        userFirstName: userDetail?.firstName || null,
                      })
                    );
                    dispatch(
                      setUserLastName({
                        userLastName: userDetail?.lastName || null,
                      })
                    );
                    dispatch(
                      setSpvDetails({
                        spvDetails: userDetail?.spvDetails || null,
                      })
                    );

                    if (redirectPath) {
                      navigate(`/${redirectPath}`);
                    } else {
                      const path = roleToPath(userDetail);

                      if (path) {
                        navigate(path);
                      } else {
                        dispatch(resetAuthState());
                        toast.warn("No action required at this time");
                      }
                    }
                  },
                }
              );
              modalAction.close();
            }
          },
          onError: (e) => {
            if (
              (e as Error).message ===
              "User is not verified for two factor authentication"
            ) {
              const otpError: HTMLElement | null =
                document.getElementById("otpError");
              if (otpError) {
                otpError.innerHTML =
                  "<span style='color: red;'>" +
                  "The code you entered is incorrect. Please try again.</span>";
              }
            } else {
              toast.error((e as Error).message);
            }
          },
        }
      );
    }
  };

  return (
    <>
      <div className={classes.gradientContainer}></div>
      <div className={classes.parentContainer}>
        <div className={classes.loginContainer}>
          <div className={classes.loginHeadingsub}>
            <div>
              <img src={Flogo} className={classes.logo} alt="" />
            </div>
          </div>
          <div className={classes.inputGroupContainer}>
            <div style={{ marginBottom: "20px" }}>
              <Text className={classes.roleLabel}>Select Role</Text>
              <div className={classes.radioButtons}>
                <label className={classes.label}>
                  <input
                    type="radio"
                    name="persona"
                    value="investor"
                    checked={persona === Persona.INVESTOR}
                    onChange={handlePersonaChange}
                  />
                  Investor
                </label>
                <label className={classes.label}>
                  <input
                    type="radio"
                    name="persona"
                    value="merchant"
                    checked={persona === Persona.MERCHANT}
                    onChange={handlePersonaChange}
                  />
                  Merchant
                </label>
                <label className={classes.label}>
                  <input
                    type="radio"
                    name="persona"
                    value="distributor"
                    checked={persona === Persona.DISTRIBUTOR}
                    onChange={handlePersonaChange}
                  />
                  Supplier
                </label>
                <label className={classes.label}>
                  <input
                    type="radio"
                    name="persona"
                    value="others"
                    checked={persona === Persona.OTHERS}
                    onChange={handlePersonaChange}
                  />
                  Others
                </label>
              </div>
            </div>
            <div className={classes.inputstyle}>
              <InputGroup
                labelText="Email"
                id="your-email"
                value={email}
                placeHolder="Enter Email Address"
                onChange={(value: string) => setEmail(value)}
                isErrored={error.email_error}
              />
            </div>

            <div className={classes.inputstyle}>
              <ForgotPasswordInput
                className="LoginForgotPasswordInput"
                name="password"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setPassword(e.target.value.trim());
                }}
                forgotPassword={() => navigate("/password-reset")}
                isErrored={error.pass_error}
              />
            </div>

            <div className={classes.buttonDiv}>
              <input
                className={classes.input}
                type="submit"
                value="Login"
                onClick={handleLogin}
              />
            </div>
            <div className={classes.signInFooter}>
              <Text className={classes.noAccount}>Don't have an account?</Text>

              <Text
                className={classes.signUp}
                onClick={() => navigate("/registration")}
              >
                Sign up
              </Text>
            </div>
            <ToastContainer />
          </div>
        </div>
      </div>
      <Modal
        classNames={{
          header: classes.modalHeader,
          title: classes.modalTitle,
        }}
        opened={openedVerifyOTP}
        onClose={modalAction.close}
        title="Two-Factor Authentication"
        color="gray"
        centered
        overlayProps={{
          blur: 2,
          opacity: 0.1,
        }}
      >
        <OtpField length={6} onChange={onChange} onEnter={handleVerifyOtp} />
        <div
          style={{
            textAlign: "center",
            fontSize: "10px",
            marginBottom: "20px",
          }}
        >
          <span id="otpError"></span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "20px",
            marginBottom: "50px",
          }}
        >
          <LargeBtn
            onClick={handleVerifyOtp}
            backgroundColor={color.FHGREEN}
            textColor="#FFFFFF"
            width="5%"
            disabled={otp !== "" ? false : true}
          >
            Verify
          </LargeBtn>
        </div>
      </Modal>
    </>
  );
};

export default Login;
